import { Api } from "./Api";
import { ApiMetadata  } from "./apiMetadata/ApiMetadata";
import { ApiMetadataRetriever } from "./apiMetadata/ApiMetadataRetriever";
import { ArrayUtil } from "./ArrayUtil";
import { Collection } from "./Collection";
import { ConversionUtil } from "./ConversionUtil";
import { Currency } from "./Currency";
import { CurrencyUtil } from "./CurrencyUtil";
import { DataType } from "./apiMetadata/DataType";
import { DateUtil } from "./Date";
import { Distance } from "./numberwithunit/Distance";
import { ErrorHandler } from "./ErrorHandler";
import { FieldUpdateEvent } from "./FieldUpdateEvent";
import { GeneralSettings } from "./settings/GeneralSettings";
import { Length } from "./numberwithunit/Length";
import { LimitedLengthArray } from "./LimitedLengthArray";
import { LogManager } from "./logging/LogManager";
import { LookupModelData } from "./LookupModelData";
import { MetadataField } from "./apiMetadata/MetadataField";
import { Model } from "./Model";
import { NumberUtil } from "./NumberUtil";
import {
    NumberWithUnit, NumberWithUnitConstructor, NumberWithUnitProps, NumberWithUnitSource
} from "./numberwithunit/NumberWithUnit";
import { NumberWithUnitPeerField } from "./numberwithunit/NumberWithUnitPeerField";
import { ObjectUtil } from "./ObjectUtil";
import { RowUpdateEvent } from "./RowUpdateEvent";
import { StringUtil } from "./StringUtil";
import { SUCCESS, SuccessFail } from "./SuccessFail";
import { Temperature } from "./numberwithunit/Temperature";
import { Volume } from "./numberwithunit/Volume";
import { Weight } from "./numberwithunit/Weight";

const log = LogManager.getLogger("core.ModelRow");

export type FieldUpdateListener = (event: FieldUpdateEvent) => void;
export type RowPostListener = (event: RowUpdateEvent) => Promise<any>;

export interface LinkedModel {
    model: string;
    rows: ModelRow[];
    deletedRows?: ModelRow[];
}

export enum ModelRowType {
    NORMAL = "normal",
    LOOKUP_MODEL_DATA = "lookupModelData"
}

class RecentChanges {
    timestamp: number;
    fieldName: string;
    incomingOldValue: string;
    oldValue: string;
    newValue: string;

    constructor(timestamp: number, fieldName:string, incomingOldValue:string, oldValue: string, newValue: string){
        this.timestamp=timestamp;
        this.fieldName=fieldName;
        this.incomingOldValue=incomingOldValue;
        this.oldValue=oldValue;
        this.newValue=newValue;
    }
}

export class ModelRow<DataInterface = any>  {
    private _data: DataInterface = {} as DataInterface;
    private _lookupModelData: LookupModelData;
    private _originalData: DataInterface;
    _modelPath: string;
    _appending: boolean;
    private linkedModels: LinkedModel[];
    private _beforePostListeners: RowPostListener[];
    private _afterPostListeners: RowPostListener[];
    private _beforeFieldUpdateListeners: FieldUpdateListener[];
    private _afterFieldUpdateListeners: FieldUpdateListener[];
    private _type: ModelRowType;
    private _recentChanges: LimitedLengthArray<RecentChanges>;
    constructor(modelPath: string, appending: boolean = true, values?: Partial<DataInterface>) {
        this._modelPath = modelPath;
        this._appending = appending;
        this._lookupModelData = new LookupModelData(this);
        if (values != null)
            this.setValues(values);
    }

    withLookupModelDataType(): ModelRow<DataInterface> {
        this._type = ModelRowType.LOOKUP_MODEL_DATA;
        return this;
    }

    /**
     * This method fetches the default values for a given ModelRow from the server
     * and populates those values into this row.  Keep in mind that it's an
     * asynchronous method and the default values won't be populated until
     * the Promise is resolved.
     * @returns a Promise that resolves to an object containing just the values were
     * set on the ModelRow
     */
    async populateDefaultValues(fieldList?: string, defaultSearchData = false): Promise<DataInterface> {
        if (StringUtil.isEmptyString(this._modelPath) === true)
            return null;
        const result = await Model.getDefaultRow<DataInterface>(this._modelPath, fieldList, defaultSearchData);
        log.debug(() => ["Default values", result]);
        this.setValues(result);
        return result;
    }

    public get originalData(): DataInterface {
        return this._originalData;
    }

    get data(): DataInterface {
        return this._data;
    }

    public get(field: keyof DataInterface, valueIfNull?: any): any {
        return this._data[field] ?? valueIfNull;
    }

    public getString(field: keyof DataInterface, valueIfNull: string = null): string {
        const result = this.get(field, valueIfNull);
        if (typeof result !== "string")
            return undefined;
        return result;
    }

    public getBoolean(field: keyof DataInterface, valueIfNull: boolean = false): boolean {
        const result = this.get(field, valueIfNull);
        if (typeof result === "string")
            return ConversionUtil.parseBoolean(result);
        return result;
    }

    public getNumber(field: keyof DataInterface, valueIfNull: number = null): number {
        const result = this.get(field, valueIfNull);
        if (typeof result === "string") {
            return Number(result);
        }
        if (typeof result !== "number") {
            return NaN;
        }
        return result;
    }

    public getDate(field: keyof DataInterface, valueIfNull: Date = null): Date {
        const result = this.get(field, valueIfNull);
        if (typeof result === "string" || result instanceof Date)
            return DateUtil.parseDate(result);
        return undefined;
    }

    public getDateTime(field: keyof DataInterface, valueIfNull: Date = null): Date {
        const result = this.get(field, valueIfNull);
        if (typeof result === "string" || result instanceof Date)
            return DateUtil.parseDateTime(result);
        return undefined;
    }

    public getCurrency(field: keyof DataInterface, valueIfNull?: number | Currency): Currency {
        const fieldData = this.get(field);
        const rateField = (field.toString() + "_r") as keyof DataInterface;
        const currencyCodeField = (field.toString() + "_c") as keyof DataInterface;
        const dateField = (field.toString() + "_d") as keyof DataInterface;
        const transactionalAmountField = (field.toString() + "_n") as keyof DataInterface;
        if (typeof fieldData === "number") {
            const props: any = {};
            if (rateField != null) {
                props.conversion_rate = this.getNumber(rateField, null);
            }
            if (currencyCodeField != null) {
                props.currency_code = this.getString(currencyCodeField);
            }
            if (dateField != null) {
                props.conversion_date = this.getDate(dateField);
            }
            if (transactionalAmountField != null) {
                props.amount = this.getNumber(transactionalAmountField, 0);
            }
            props.base_amount = fieldData;
            return new Currency(props);
        }
        else if (typeof fieldData === "object") {
            return new Currency(fieldData);
        }

        return valueIfNull instanceof Currency ? valueIfNull : Currency.createCurrencyWithDefaults(valueIfNull, this.getString(currencyCodeField));
    }

    public updateCurrency(field: keyof DataInterface, currencyOrAmount: number | Currency | null, originator?: any): void {
        if (typeof currencyOrAmount === "number") {
            const updatedCurrency = this.getCurrency(field, currencyOrAmount);
            updatedCurrency.setAmount(currencyOrAmount);
            this.set(field, updatedCurrency, originator);
            const nativeAmountField = (field.toString() + "_n") as keyof DataInterface;
            this.set(nativeAmountField, currencyOrAmount);
        } else {
            this.set(field, currencyOrAmount, originator);
            const rateField = (field.toString() + "_r") as keyof DataInterface;
            this.set(rateField, currencyOrAmount?.conversion_rate);
            const currencyCodeField = (field.toString() + "_c") as keyof DataInterface;
            this.setValueAndLookupModelData(currencyCodeField, currencyOrAmount?.currency_code, { currency_type_id: currencyOrAmount?.currency_code });
            const dateField = (field.toString() + "_d") as keyof DataInterface;
            this.set(dateField, currencyOrAmount?.conversion_date);
            const nativeAmountField = (field.toString() + "_n") as keyof DataInterface;
            this.set(nativeAmountField, currencyOrAmount?.amount);
        }
    }

    public getDistance = (field: keyof DataInterface, valueIfNull?: NumberWithUnit|number): NumberWithUnit => {
        return this.getNumberWithUnit(field, valueIfNull, Distance);
    }

    public getLength = (field: keyof DataInterface, valueIfNull?: NumberWithUnit|number): NumberWithUnit => {
        return this.getNumberWithUnit(field, valueIfNull, Length);
    }

    public getTemperature = (field: keyof DataInterface, valueIfNull?: NumberWithUnit|number): NumberWithUnit => {
        return this.getNumberWithUnit(field, valueIfNull, Temperature);
    }

    public getVolume = (field: keyof DataInterface, valueIfNull?: NumberWithUnit|number): NumberWithUnit => {
        return this.getNumberWithUnit(field, valueIfNull, Volume);
    }

    public getWeight = (field: keyof DataInterface, valueIfNull?: NumberWithUnit|number): NumberWithUnit => {
        return this.getNumberWithUnit(field, valueIfNull, Weight);
    }

    /**
     * Returns a NumberWithUnit object for the given field. If the field already stores a NumberWithUnit json object, it
     * will be wrapped/normalized into the correct type of NumberWithUnit object.
     *
     * If the field stores a primitive number, a new NumberWithUnit will be created.
     */
    private getNumberWithUnit(field: keyof DataInterface, valueIfNull: NumberWithUnit|number,
        cls: NumberWithUnitConstructor): NumberWithUnit {
        const fieldData: any = this.get(field);
        if (fieldData instanceof NumberWithUnit) {
            return fieldData;
        }

        const fieldName = field as string;
        const unitFieldName = fieldName + NumberWithUnitPeerField.UNIT;
        const unit = this.getString(unitFieldName as keyof DataInterface);
        const functionalNumberKey = fieldName + NumberWithUnitPeerField.FUNCTIONAL_NUMBER;
        const functionalNumber = this.getNumber(functionalNumberKey as keyof DataInterface);
        const functionalUnitKey = fieldName + NumberWithUnitPeerField.FUNCTIONAL_UNIT;
        const functionalUnit = this.getString(functionalUnitKey as keyof DataInterface);

        if (fieldData != null) {
            if (typeof fieldData === "object") {
                // Try to interpret as NumberWithUnitProps
                const props = fieldData as NumberWithUnitProps;
                return new cls({
                    number: props.number,
                    unit: props.unit,
                    functionalNumber: props.functionalNumber ?? functionalNumber,
                    functionalUnit: props.functionalUnit ?? functionalUnit
                });
            }
            if (typeof fieldData === "number") {
                return new cls({
                    number: fieldData,
                    unit,
                    functionalNumber,
                    functionalUnit
                });
            }
        }
        if (valueIfNull != null) {
            if (valueIfNull instanceof NumberWithUnit) {
                return valueIfNull;
            }
            else if (typeof valueIfNull === "number") {
                return new cls({
                    number: valueIfNull,
                    unit,
                    functionalNumber,
                    functionalUnit
                });
            }
        }
        return null;
    }

    public updateDistance(field: keyof DataInterface, value: NumberWithUnitSource | null, originator?: any) {
        this.updateNumberWithUnit(field, value, originator, Distance, this.getDistance);
    }

    public updateLength(field: keyof DataInterface, value: NumberWithUnitSource | null, originator?: any) {
        this.updateNumberWithUnit(field, value, originator, Length, this.getLength);
    }

    public updateTemperature(field: keyof DataInterface, value: NumberWithUnitSource | null, originator?: any) {
        this.updateNumberWithUnit(field, value, originator, Temperature, this.getTemperature);
    }

    public updateVolume(field: keyof DataInterface, value: NumberWithUnitSource | null, originator?: any) {
        this.updateNumberWithUnit(field, value, originator, Volume, this.getVolume);
    }

    public updateWeight(field: keyof DataInterface, value: NumberWithUnitSource | null, originator?: any) {
        this.updateNumberWithUnit(field, value, originator, Weight, this.getWeight);
    }

    /**
     * Updates the field with a NumberWithUnit value. If a number is provided, unit/unitSystem must be supplied to
     * construct a NumberWithUnit instance for storage.
     */
    private updateNumberWithUnit(field: keyof DataInterface,
        value: NumberWithUnitSource | null,
        originator: any,
        cls: NumberWithUnitConstructor,
        getter: (field: keyof DataInterface, valueIfNull?: NumberWithUnit|number) => NumberWithUnit): void {

        if (value == null) {
            this.set(field, null, originator);
            return;
        }

        let numberWithUnit: NumberWithUnit;
        if (value instanceof NumberWithUnit) {
            numberWithUnit = value;
        } else if (typeof value === "number") {
            numberWithUnit = getter(field, 0);
            numberWithUnit.number = value;
        } else {
            numberWithUnit = new cls(value as NumberWithUnitProps);
        }

        this.set(field, numberWithUnit, originator);

        const functionalUnit = numberWithUnit.getCompanyFunctionalUnit();
        if (functionalUnit != null && numberWithUnit.number != null) {
            const functionalNumber = numberWithUnit.getNumberIn(functionalUnit);
            numberWithUnit.functionalNumber = functionalNumber;
            numberWithUnit.functionalUnit = functionalUnit;
        }
    }

    public getTypedDataValue(field: keyof DataInterface): any {
        const metadata = this.getMetadata();
        const dataType = metadata?.output?.[field as string]?.dataType;
        switch (dataType) {
            case DataType.STRING:
                return this.getString(field);
            case DataType.INT:
            case DataType.FLOAT:
                return this.getNumber(field);
            case DataType.BOOL:
                return this.getBoolean(field);

            case DataType.DATERANGE:
                return this.getDateTime(field);
            case DataType.DATE:
                return this.getDate(field);

            case DataType.CURRENCY:
                return this.getCurrency(field);

            case DataType.DISTANCE_WU:
                return this.getDistance(field);
            case DataType.LENGTH_WU:
                return this.getLength(field);
            case DataType.TEMPERATURE_WU:
                return this.getTemperature(field);
            case DataType.VOLUME_WU:
                return this.getVolume(field);
            case DataType.WEIGHT_WU:
                return this.getWeight(field);

            default:
                return this.get(field);
        }
    }

    /**
     * Sets either a single field's value or multiple values in one call.
     *
     * @param values
     * If values is a string, this will set that single fields' value to valueToSetWhenSingleField.
     * If values is a object, all that object's key/value pairs will be applied to the ModelRow's data.
     * @param valueToSetWhenSingleField
     * @param originator The object (usually a Component) that triggered the update
     */
    set(values: Partial<DataInterface> | keyof DataInterface, valueToSetWhenSingleField?: any, originator?: any): SuccessFail {
        if (!this._appending && this._originalData == null)
            this.resetOriginalData();
        if (typeof values === "string")
            return this._internalSet(values, valueToSetWhenSingleField, originator);
        else {
            if (valueToSetWhenSingleField != null)
                throw new Error("DataRow.set() second argument should not be used when the first argument is an object.");
            return this.internalSetValues(values, true, originator);
        }
    }

    protected _internalSet(field: string, value: any, originator: any): SuccessFail {
        const event = this.fireListeners(this._beforeFieldUpdateListeners, () => new FieldUpdateEvent(this, field, oldValue, this._data[field], true, originator));
        if (event?.preventReason != null) {
            log.debug(() => ["beforeFieldUpdateListeners prevented update", event.preventReason, this, field, value, originator]);
            return new SuccessFail(false, event.preventReason);
        }
        const oldValue = this._data[field];
        if (value === undefined) {
            delete this._data[field];
        } else {
            this._data[field] = value;
        }
        if (value == null)
            this._lookupModelData.delete(field);
        this.fireListeners(this._afterFieldUpdateListeners, () => new FieldUpdateEvent(this, field, oldValue, this._data[field], false, originator));
        return SUCCESS;
    }

    public clearLinkedModels() {
        this.linkedModels = null;
    }

    public addLinkedModel(value: LinkedModel) {
        if (this.linkedModels == null)
            this.linkedModels = [];
        this.linkedModels.push(value);
    }

    public isEmpty(): boolean {
        return this._data == null || Object.keys(this._data).length === 0;
    }

    public clear() {
        if (!this.isEmpty()) {
            for (const key of Object.keys(this._data))
                delete this._data[key];
        }
        this.removeLookupModelData();
    }

    public addSingleQuotes() {
        for (const key in this) {
            if (key === "_modelPath" || key === "_appending") {
                continue;
            }
            const value = this[key];
            if (typeof value === "string") {
                let modifiedValue: string = value;
                if (modifiedValue.substr(0, 1) !== "'") {
                    modifiedValue = "'" + modifiedValue;
                }
                if (modifiedValue.substr(modifiedValue.length - 1, 1) !== "'") {
                    modifiedValue += "'";
                }
                this[key] = modifiedValue as any;
            }
        }
    }

    public toJSON(): any {
        const result: any = { ...this._data, _appending: this._appending };
        if (this.linkedModels?.length > 0)
            result._child_links = this.linkedModels;
        return result;
    }

    addBeforeFieldUpdateListener(listener: FieldUpdateListener) {
        if (this._beforeFieldUpdateListeners == null) {
            this._beforeFieldUpdateListeners = [];
        }
        this._beforeFieldUpdateListeners.push(listener);
    }

    removeBeforeFieldUpdateListener(listener: FieldUpdateListener) {
        const index = this._beforeFieldUpdateListeners?.indexOf(listener) ?? -1;
        if (index >= 0) {
            this._beforeFieldUpdateListeners.splice(index, 1);
        }
        if (ArrayUtil.isEmptyArray(this._beforeFieldUpdateListeners)) {
            this._beforeFieldUpdateListeners = null;
        }
    }

    addAfterFieldUpdateListener(listener: FieldUpdateListener) {
        if (this._afterFieldUpdateListeners == null) {
            this._afterFieldUpdateListeners = [];
        }
        this._afterFieldUpdateListeners.push(listener);
    }

    removeAfterFieldUpdateListener(listener: FieldUpdateListener) {
        const index = this._afterFieldUpdateListeners?.indexOf(listener) ?? -1;
        if (index >= 0) {
            this._afterFieldUpdateListeners.splice(index, 1);
        }
        if (ArrayUtil.isEmptyArray(this._afterFieldUpdateListeners)) {
            this._afterFieldUpdateListeners = null;
        }
    }

    protected fireListeners(listeners: any[], eventCreatorFunction: () => FieldUpdateEvent): RowUpdateEvent {
        if (listeners != null && listeners.length > 0) {
            const event = eventCreatorFunction();
            for (const listener of listeners)
                listener(event);
            return event;
        }
        return undefined;
    }

    addBeforePostListener(listener: RowPostListener) {
        if (this._beforePostListeners == null)
            this._beforePostListeners = [];
        if (this._beforePostListeners.indexOf(listener) < 0)
            this._beforePostListeners.push(listener);
    }

    clearBeforePostListeners() {
        this._beforePostListeners = null;
    }

    addAfterPostListener(listener: RowPostListener) {
        if (this._afterPostListeners == null)
            this._afterPostListeners = [];
        if (this._afterPostListeners.indexOf(listener) < 0)
            this._afterPostListeners.push(listener);
    }

    clearAfterPostListeners() {
        this._afterPostListeners = null;
    }

    setValues(values: Partial<DataInterface>, originator?: any): ModelRow<DataInterface> {
        this.internalSetValues(values, false, originator);
        return this;
    }

    private internalSetValues(values: any, returnOnFailure = false, originator?: any): SuccessFail {
        if (ObjectUtil.isObject(values) !== true)
            return SUCCESS;

        for (const [key, value] of Object.entries(values)) {
            if (key.startsWith(LookupModelData.LOOKUP_MODEL_PREFIX)) {
                if (typeof value === "object" && !ObjectUtil.isEmptyObject(value)) {
                    const actualField = key.substring(LookupModelData.LOOKUP_MODEL_PREFIX.length);
                    this._lookupModelData.set(actualField, value, false);
                }
            } else {
                const result = this._internalSet(key, value, originator);
                if (returnOnFailure && !result.success)
                    return result;
            }
        }

        this._lookupModelData.resetOriginalDataIfNeeded();
        return SUCCESS;
    }

    protected async beforePost(): Promise<SuccessFail> {
        if (this._beforePostListeners != null) {
            const event = new RowUpdateEvent(this, true, null);
            for (const listener of this._beforePostListeners) {
                await listener(event);
                if (event.preventReason != null)
                    return Promise.resolve(new SuccessFail(false, event.preventReason, event.onPrevent));
            }
        }
        return Promise.resolve(SUCCESS);
    }

    protected async afterPost(response: any, fieldList?: string): Promise<any> {
        const data = response?.data?.[0]?.data?.[0];
        if (data != null) {
            if (!StringUtil.isEmptyString(fieldList)) {
                // if a fieldList was sent, the response should contain all the lookup model data
                this._lookupModelData = new LookupModelData(this);
            }
            this.setValues(data);
        }
        if (this.linkedModels != null)
            this.linkedModels = null;
        this._appending = false;
        this.resetOriginalData();
        if (this._afterPostListeners != null) {
            const event = new RowUpdateEvent(this, true, null);
            for (const listener of this._afterPostListeners)
                await listener(event);
        }
    }

    resetOriginalData(field?: string) {
        const resetOriginalLmData = this._originalData != null;
        if (field == null) {
            this._originalData = ObjectUtil.deepCopy(this._data);
        } else if (this._originalData != null) { //could be null in add mode
            this._originalData[field] = ObjectUtil.deepCopy(this._data[field]);
        }
        if (resetOriginalLmData)
            this._lookupModelData.resetOriginalData(field);
    }

    resetOriginalLookupModelData(field?: string, onlyWhenNull: boolean = false) {
        if (onlyWhenNull) {
            if (field != null) {
                if (this._lookupModelData.originalData?.get(field) == null)
                    this._lookupModelData.resetOriginalData(field);
            } else if (this._lookupModelData.originalData == null) {
                this._lookupModelData.resetOriginalData();
            }
        } else {
            this._lookupModelData.resetOriginalData(field);
        }
    }

    revertToOriginalData(fields?: string | string[], originator?: any) {
        if (this._originalData == null)
            return;

        if (fields == null) {
            this.clear();
            this._lookupModelData.revertToOriginalData();
            this.setValues(this._originalData, originator);
        } else {
            ArrayUtil.getAsArray(fields).forEach(field => {
                this._lookupModelData.revertToOriginalData(field);
                this._internalSet(field, this._originalData[field], originator);
            });
        }
    }

    public getBodyWithLinkedModels() {
        const body = { ...this._data };
        this.populateLinkedModels(body);
        return body;
    }

    async post(errorHandler?: ErrorHandler, fieldList?: string): Promise<ModelRow> {
        const beforePostSuccessFail = await this.beforePost();
        if (!beforePostSuccessFail.success)
            if (beforePostSuccessFail.onFail)
                return Promise.reject(beforePostSuccessFail.onFail);
            else
                return Promise.reject(beforePostSuccessFail.reason);
        if (this._appending) {
            const body = this._data;
            this.populateLinkedModels(body);
            if (fieldList != null)
                body["_field_list"] = {layoutName: fieldList};
            return Api.post(this._modelPath, body, null, errorHandler).then(async response => {
                await this.afterPost(response, fieldList);
                return this;
            });
        }
        else {
            await ApiMetadataRetriever.get(this._modelPath);
            const keyData = this.getKeyData();
            const changedData = this.getChangedData();
            const body: any = { ...changedData };

            this.populateOriginalData(body, changedData);

            this.populateLinkedModels(body);
            if (Object.keys(body).length > 0) {
                if (fieldList != null)
                    body["_field_list"] = { layoutName: fieldList };
                return Api.update(this._modelPath, { ...keyData, ...body }, null, errorHandler).then(async response => {
                    await this.afterPost(response, fieldList);
                    return this;
                });
            }
            else {
                log.debug(() => ["Not posting unchanged row with keyData", keyData]);
                return Promise.resolve(null); //return a null in place of the response object
            }
        }
    }

    protected populateLinkedModels(body: any) {
        if (this.linkedModels != null) {
            let bodyLinkArray: LinkedModel[];
            for (const link of this.linkedModels) {
                let modelLink: LinkedModel;
                if (link.deletedRows != null) {
                    for (const row of link.deletedRows) {
                        if (bodyLinkArray == null) {
                            bodyLinkArray = [];
                            body._child_links = bodyLinkArray;
                        }
                        if (modelLink == null) {
                            modelLink = { model: link.model, rows: [] };
                            bodyLinkArray.push(modelLink);
                        }
                        if (modelLink.deletedRows == null)
                            modelLink.deletedRows = [];
                        modelLink.deletedRows.push(new ModelRow(link.model, false, { ...row.getKeyData() }))
                    }
                }
                for (const row of link.rows) {
                    const changedData = row.getChangedData();
                    if (changedData != null && Object.keys(changedData).length > 0) {
                        if (bodyLinkArray == null) {
                            bodyLinkArray = [];
                            body._child_links = bodyLinkArray;
                        }
                        if (modelLink == null) {
                            modelLink = { model: link.model, rows: [] };
                            bodyLinkArray.push(modelLink);
                        }
                        const rowPayload: any = { ...row.getKeyData(), ...changedData };
                        row.populateOriginalData(rowPayload, changedData);
                        modelLink.rows.push(new ModelRow(link.model, row._appending, rowPayload));
                    }
                }
            }
        }
    }

    getMetadata() {
        return ApiMetadataRetriever.getFromCache(this._modelPath);
    }

    getMetadataPromise(): Promise<ApiMetadata> {
        return ApiMetadataRetriever.get(this._modelPath);
    }

    hasKeyFields(): boolean {
        if (StringUtil.isEmptyString(this._modelPath))
            return false;
        return !ArrayUtil.isEmptyArray(this.getMetadata()?.keyFields);
    }

    getKeyData(): Collection<unknown> {
        const meta: ApiMetadata = this.getMetadata();
        if (meta == null)
            throw new Error("This row has no metadata - " + this._modelPath);
        const result = {};
        if (meta.keyFields == null)
            throw new Error("This row has no key data - " + this._modelPath);
        for (const fieldName of meta.keyFields)
            result[fieldName] = this._data[fieldName];
        return result;
    }

    getChangedData(): Partial<DataInterface> {
        return this.compareWithData(this._originalData);
    }

    private populateOriginalData(targetBody: any, changedData: Partial<DataInterface>) {
        if (this._originalData == null || changedData == null || !GeneralSettings.getSingleton().isDataConflictDetectionEnabled()) {
            return;
        }

        const originalValues: any = {};
        for (const key of Object.keys(changedData)) {
            originalValues[key] = this._originalData[key];
        }

        if (!ObjectUtil.isEmptyObject(originalValues)) {
            targetBody._original_data = originalValues;
        }
    }

    compareWithData(otherData: DataInterface): Partial<DataInterface> {
        if (this._appending)
            return this._data;
        if (otherData == null)
            return {};
        const result = {};
        const meta: ApiMetadata = this.getMetadata();

        for (const [key, value] of Object.entries(this._data)) {
            const other = otherData[key];
            if (!this.fieldsAreEqual(meta.output[key], other, value))
                result[key] = value;
        }
        return result;
    }

    hasChanged(newRecordsChanged: boolean = true): boolean {
        if (this._appending)
            return newRecordsChanged;

        if (ObjectUtil.isEmptyObject(this._originalData))
            return false;

        return !this.isDataEqual(this._originalData);
    }

    isDataEqual(otherData: DataInterface, excludeFields?: string[]): boolean {
        if (ObjectUtil.isEmptyObject(this._data) && ObjectUtil.isEmptyObject(otherData))
            return true;

        if (ObjectUtil.isEmptyObject(otherData))
            return false;

        const meta: ApiMetadata = this.getMetadata();
        for (const [key, value] of Object.entries(this._data)) {
            if (excludeFields?.includes(key)) {
                continue;
            }
            const orig = otherData[key];
            const equal = this.fieldsAreEqual(meta?.output[key], orig, value);
            if (!equal) {
                return false;
            }
        }
        return true;
    }

    public fieldsAreEqual(field: MetadataField, value1: any, value2: any) {
        if (value1 == null && value2 == null)
            return true;
        else if (value1 == null || value2 == null)
            return false;
        if (field != null) {
            if (field.dataType === DataType.TIME)
                return DateUtil.timesEqual(value1, value2);
            if (field.dataType === DataType.DATE)
                return DateUtil.datesEqual(value1, value2);
            if (field.dataType === DataType.DATETIME)
                return DateUtil.dateTimesEqual(value1, value2);
            if (field.dataType === DataType.CURRENCY)
                return CurrencyUtil.currenciesAreEqual(value1, value2);
            if (field.dataType === DataType.FLOAT || field.dataType === DataType.INT)
                return NumberUtil.areEqual(value1, value2);
            if (field.dataType === DataType.LIST || field.dataType === DataType.OBJECT)
                return ObjectUtil.deepEqual(value1, value2);
        }
        if (value1 instanceof Object && value2 instanceof Object) {
            return ObjectUtil.deepEqual(value1, value2);
        }
        return value1 == value2;
    }

    delete(errorHandler?: ErrorHandler): Promise<any> {
        return Api.delete(this._modelPath, this.data, null, errorHandler);
    }

    get type(): ModelRowType {
        return this._type == null ? ModelRowType.NORMAL : this._type;
    }

    set type(value: ModelRowType) {
        this._type = value;
    }

    get recentChanges(): RecentChanges[] {
        if( this._recentChanges==null){
            return [];
        }
        return this._recentChanges.getAll();
    }

    set recentChanges(changes:RecentChanges[]){
        this._recentChanges=new LimitedLengthArray<RecentChanges>(30);
        changes.forEach(element => {
            this._recentChanges.push(element);
        });
    }

    public createBasicCopy(copyLookupModelData:boolean = true): ModelRow {
        const copy = new ModelRow(this._modelPath, this._appending, ObjectUtil.deepCopy(this._data));
        copy.type = this.type;
        if (copy.type !== ModelRowType.LOOKUP_MODEL_DATA && copyLookupModelData)
            copy._lookupModelData = this._lookupModelData.createBasicCopy();
        return copy;
    }

    public createLookupModelDataCopy(): LookupModelData {
        return this._lookupModelData.createBasicCopy();
    }

    public isNull(fieldName: keyof DataInterface): boolean {
        const value = this.get(fieldName, undefined);
        if (value != null) {
            if (typeof value === "string")
                return StringUtil.isEmptyString(value);
            return false;
        } else {
            return true;
        }
    }

    setLookupModelData(field: keyof DataInterface, value: any) {
        if (typeof field === "string")
            this._lookupModelData.set(field, value);
    }

    addLookupModelData(field: keyof DataInterface, value: any) {
        this.setLookupModelData(field, [...this.getLookupModelData(field) ?? [], value]);
    }

    setValueAndLookupModelData(field: keyof DataInterface, value: any, lmData: any, originator?: any): SuccessFail {
        let result = null;
        if (typeof field === "string") {
            this.setLookupModelData(field, lmData);
            result = this.set(field, value, originator);
        }
        return result;
    }

    setValuesAndLookupModelData(sourceRow: ModelRow, replaceLookupModelData: boolean = false) {
        this._lookupModelData.syncValues(sourceRow.createLookupModelDataCopy(), replaceLookupModelData);
        this.set(sourceRow.data);
    }

    getLookupModelData(field: keyof DataInterface): ModelRow<any>[] {
        if (typeof field === "string") {
            return this._lookupModelData.get(field);
        }
        return undefined;
    }

    getFirstLookupModelData(field: keyof DataInterface, valueIfNull?: ModelRow<any>): ModelRow<any> {
        const lmArray = this.getLookupModelData(field);
        return ArrayUtil.getFirstElement(lmArray) ?? valueIfNull;
    }

    hasLookupModelData(field: keyof DataInterface): boolean {
        return this.getLookupModelData(field) != null;
    }

    isLookupModelDataRow(): boolean {
        return this.type === ModelRowType.LOOKUP_MODEL_DATA;
    }

    removeLookupModelData(field?: keyof DataInterface) {
        if (field == null) {
            this._lookupModelData.clear();
        } if (typeof field === "string") {
            this._lookupModelData.delete(field);
        }
    }

    public addRecentChange(timestamp:number, fieldName:string, incomingOldValue:any, oldValue:any, newValue:any){
        if(CurrencyUtil.isCurrency(incomingOldValue)){
            incomingOldValue=CurrencyUtil.formatCurrency(incomingOldValue);
        }
        if(CurrencyUtil.isCurrency(oldValue)){
            oldValue=CurrencyUtil.formatCurrency(oldValue);
        }
        if(CurrencyUtil.isCurrency(newValue)){
            newValue=CurrencyUtil.formatCurrency(newValue);
        }
        if(this._recentChanges == null){
            this._recentChanges = new LimitedLengthArray<RecentChanges>(30);
        }
        this._recentChanges.push(new RecentChanges(timestamp, fieldName, incomingOldValue, oldValue, newValue));
    }
}
